import { Button } from 'react-bootstrap';
import React, { useEffect, useRef, useState } from 'react';
import { toastError, toastWarning } from '../util/ToastifyUtil';
import { translateText } from '../api/translate';
import { unescapeHtmlEntities } from '../util/StringUtil';
import { handleApiError } from '../util/ErrorHandlerUtil';
import { useAuth0 } from '@auth0/auth0-react';
import { useStore } from '../store/store';
import { Gear } from 'react-bootstrap-icons';
import { MESSAGE_TYPE } from '../types/ExtMessage';

const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
const extensionId = process.env.REACT_APP_EXTENSION_ID;

const TranslateButton = () => {
	const { logout } = useAuth0();
	const {
		isLoading,
		auth0TokenId,
		selectedConsumerId,
		selectedSourceLocale,
		selectedTargetLocale,
		textBefore,
		initialTextToTranslate,
		autoTranslate,
		consumerLocales: languages,
		rnResponseProjectId,
		setRnResponseProjectId,
		setAutoTranslate,
		setTextAfter,
		autoDetectLang,
		setSelectedSourceLocale,
		setLoading
	} = useStore((state) => state);

	const [isTranslationLoading, setIsTranslationLoadingLoading] = useState(false);
	const languageOptionDefault = 0;

	/**
	 * SV-568 - Automatically perform text translation when a user selects the "Language I/O - Translate" option from the right-click context menu in the Chrome extension.
	 */
	useEffect(() => {
		// Run the translation process only when all the required parameters are populated and the autoTranslate flag is true.
		// This flag ensures that the user's automatic translation, is only triggered through the right-click context menu.
		const canRunAutoTranslate = autoTranslate && initialTextToTranslate !== '' && textBefore !== '' && selectedSourceLocale !== 0 && selectedTargetLocale !== 0 && selectedConsumerId !== null;

		if (canRunAutoTranslate) {
			onTranslate();
		}
	}, [selectedSourceLocale, autoTranslate, initialTextToTranslate]);

	const onTranslate = async () => {
		const hasSelectedFromLanguage = selectedSourceLocale && selectedSourceLocale !== languageOptionDefault;
		const hasSelectedToLanguage = selectedTargetLocale && selectedTargetLocale !== languageOptionDefault;
		const isValid =
			textBefore &&
			textBefore !== '' &&
			hasSelectedFromLanguage &&
			hasSelectedToLanguage;
		if (isValid) {
			setIsTranslationLoadingLoading(true);
			try {
				let fromLocale = languages.find(i => i.lioLocaleId === selectedSourceLocale);
				if (!fromLocale) {
					const apiResponse = await autoDetectLang(textBefore, auth0TokenId, selectedConsumerId);
					if (!apiResponse.hasError) {
						fromLocale = languages.find(i => i.consumerLocaleId === apiResponse.data);
						setSelectedSourceLocale(fromLocale?.lioLocaleId!)
					}
				}
				const toLocale = languages.find(i => i.lioLocaleId === selectedTargetLocale);
				const response = await translateText(
					textBefore,
					fromLocale?.consumerLocaleId,
					toLocale?.consumerLocaleId,
					rnResponseProjectId,
					auth0TokenId,
					selectedConsumerId
				);

				if (response.hasError) {
					if (response.error instanceof Error) {
						throw response.error; // Rethrow the original Error object
					} else {
						throw new Error('An error occurred during translation');
					}
				}

				const translatedResponse = response.data;
				const unescapedHtmlEntitiesTranslatedText = unescapeHtmlEntities(translatedResponse.translatedText);
				setTextAfter(unescapedHtmlEntitiesTranslatedText);
				setRnResponseProjectId(translatedResponse.rnResponseProjectId);
			} catch (error: any) {
				const { message, shouldLogout } = handleApiError(error)
				if (shouldLogout) {
					logout({
						clientId: clientId
					});
					if (window.chrome && window.chrome.runtime) {
						window.chrome.runtime.sendMessage(extensionId!, { type: 'appLogout', data: null }, () => {
							console.debug('Extension logout successful');
						});
					}
				} else {
					toastError(message);
					setTextAfter('');
				}

			} finally {
				setIsTranslationLoadingLoading(false);
				setAutoTranslate(false)
				setLoading(false)
			}
		} else {
			toastWarning('Please fill out the mandatory fields.');
		}
	};

	return (
		<div>
			<div className="d-flex gap-1 align-items-center justify-content-between">
					<span data-toggle="tooltip" title="Open Settings">
						<Gear
							type="button"
							className="mx-2 icon "
							onClick={() => {
								window?.top?.postMessage({type: MESSAGE_TYPE.openPopupSettings}, "*");
							}}
						/>
					</span>
				<Button
					disabled={isTranslationLoading || isLoading}
					className="lio-button-primary translate-btn"
					variant="brand"
					type="button"
					onClick={onTranslate}
				>
					Translate
				</Button>
			</div>
		</div>
	);
};

export default TranslateButton;
